import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  FormTextField,
  FormSelect,
} from '@uncinc/uncinc-react-kitchen-sink';

import { OTHER_FORM_ITEM_ID } from '../../config/constants';

export interface ProfileFormProps {
  formRef?: any,
  fieldsToShow?: Array<string>,
  allowedJobTitleOptions?: Array<string>,
  allowedWorkFieldOptions?: Array<string>,
}

import jobTitleOptions from '../../helpers/form_options/profile_job_titles.json';
import workFieldOptions from '../../helpers/form_options/profile_work_fields.json';

import './style.scss';

export const getProfileValidationSchema = () => {
  const { t } = useTranslation();
  return {
    field_first_name: Yup.string().required(t('validation_messages.profile.first_name')),
    field_infix_name: Yup.string(),
    field_last_name: Yup.string().required(t('validation_messages.profile.last_name')),
    field_organisation_name: Yup.string().required(t('validation_messages.profile.organisation_name')),
    field_work_area: Yup.string().required(t('validation_messages.profile.work_area')),
    field_work_area_other: Yup.string().when('field_work_area', {
      is: (val: any) => val === OTHER_FORM_ITEM_ID,
      then: Yup.string().required(t('validation_messages.profile.work_area')).max(60),
      otherwise: Yup.string().notRequired(),
    }),
    field_job_title: Yup.string().required(t('validation_messages.profile.job_title')),
    field_job_title_other: Yup.string().when('field_job_title', {
      is: (val: any) => val === OTHER_FORM_ITEM_ID,
      then: Yup.string().required(t('validation_messages.profile.job_title')).max(60),
      otherwise: Yup.string().notRequired(),
    }),
  };
};

/**
 * Get the formik fields related to the userprofile, can optionally be limited to {fieldsToShow}.
 */
export const ProfileForm: React.FC<ProfileFormProps> = ({
  formRef = null,
  fieldsToShow = null,
  allowedJobTitleOptions = null,
  allowedWorkFieldOptions = null,
}) => {
  const { t } = useTranslation();
  const [workFieldOtherActive, setWorkFieldOtherActive] = useState<boolean>(false);
  const [jobTitleOtherActive, setJobTitleOtherActive] = useState<boolean>(false);

  const classNames = classnames('ProfileForm', {
    'ProfileForm--workfield_other': workFieldOtherActive,
    'ProfileForm--jobtitle_other': jobTitleOtherActive,
  });

  const filteredJobTitleOptions = allowedJobTitleOptions
    ? jobTitleOptions.filter((option) => allowedJobTitleOptions.includes(option.value))
    : jobTitleOptions;
  const filteredWorkFieldOptions = allowedWorkFieldOptions
    ? workFieldOptions.filter((option) => allowedWorkFieldOptions.includes(option.value))
    : workFieldOptions;


  useEffect(() => {
    const values = formRef?.current?.values;
    if (values?.field_work_area) {
      setWorkFieldOtherActive(values.field_work_area === OTHER_FORM_ITEM_ID);
    }
    if (values?.field_job_title) {
      setJobTitleOtherActive(values.field_job_title === OTHER_FORM_ITEM_ID);
    }
  }, [formRef]);

  const showField = (fieldName: string) => (
    !fieldsToShow || fieldsToShow.includes(fieldName));

  return (
    <div className={classNames}>
      {showField('field_first_name') && (
        <FormTextField
          title={t('profile.first_name')}
          fieldName="field_first_name"
          required
        />
      )}

      <div className="row">
        {showField('field_infix_name') && (
          <div className="col-12 col-lg-4">
            <FormTextField
              title={t('profile.infix_name')}
              fieldName="field_infix_name"
            />
          </div>
        )}

        {showField('field_last_name') && (
          <div className="col-12 col-lg-8">
            <FormTextField
              title={t('profile.last_name')}
              fieldName="field_last_name"
              required
            />
          </div>
        )}
      </div>

      {showField('field_organisation_name') && (
        <FormTextField
          title={t('profile.organisation_name')}
          fieldName="field_organisation_name"
          required
        />
      )}

      {showField('field_work_area') && (
        <FormSelect
          options={filteredWorkFieldOptions}
          fieldName="field_work_area"
          title={t('profile.work_area')}
          placeholder={t('profile.work_area_placeholder')}
          isClearable={false}
          required
          onChange={(value: any) => {
            setWorkFieldOtherActive(value === OTHER_FORM_ITEM_ID);
          }}
        />
      )}

      {showField('field_work_area_other') && (
        <div className="workfield_other">
          <FormTextField fieldName="field_work_area_other" />
        </div>
      )}

      {showField('field_job_title') && (
        <FormSelect
          options={filteredJobTitleOptions}
          fieldName="field_job_title"
          title={t('profile.job_title')}
          placeholder={t('profile.job_title_placeholder')}
          isClearable={false}
          required
          onChange={(value: any) => {
            setJobTitleOtherActive(value === OTHER_FORM_ITEM_ID);
          }}
        />
      )}

      {showField('field_job_title_other') && (
        <div className="jobtitle_other">
          <FormTextField fieldName="field_job_title_other" />
        </div>
      )}
    </div>
  );
};
